import {
  Button as ChakraButton,
  Center,
  Flex as ChakraFlex,
  Icon,
  Input as ChakraInput,
} from '@chakra-ui/react';
import './AfterInterviewDetails.css';
import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { InterviewAssistant } from '../component';
import type { InterviewAssistantHandle } from '../component/InterviewAssistant';
import { AiStar } from '@/assets/icons/interview';

// Casting Chakra components to `any` collapses their highly-recursive
// style-prop union types, which otherwise hit TS2590 ("union too complex")
// when many shorthand style props (or the `sx` object) are stacked together.
const Flex = ChakraFlex as any;
const Input = ChakraInput as any;
const Button = ChakraButton as any;

export const AfterInterviewAssistant = memo(function AfterInterviewAssistant({
  interview,
  interviewId = undefined,
  triggerContainerProps = {},
  assistantContainerProps = {},
}: {
  interview: any;
  interviewId?: string | number | undefined;
  triggerContainerProps?: any;
  assistantContainerProps?: any;
}) {
  const [assistantLoading, setAssistantLoading] = useState<boolean>(false);
  const [externalPrompt, setExternalPrompt] = useState<string>('');

  const [showInterviewAssistant, setShowInterviewAssistant] =
    useState<boolean>(false);

  const interviewAssistantRef = useRef<HTMLDivElement>(null);
  const assistantApiRef = useRef<InterviewAssistantHandle | null>(null);
  const queuedPromptRef = useRef<string>('');

  const handleCloseAssistant = useCallback(() => {
    setShowInterviewAssistant(false);
  }, []);

  const handleLoadingChange = useCallback((loading: boolean) => {
    setAssistantLoading(loading);
  }, []);

  // When assistant becomes visible and there is a queued prompt, send it.
  // Stored in a ref so the effect doesn't have to clear state synchronously.
  useEffect(() => {
    if (
      showInterviewAssistant &&
      queuedPromptRef.current &&
      assistantApiRef.current
    ) {
      const pending = queuedPromptRef.current;
      queuedPromptRef.current = '';
      assistantApiRef.current.postMessage(pending);
    }
  }, [showInterviewAssistant]);

  const handleSubmitPrompt = useCallback(() => {
    setExternalPrompt((prev) => {
      const message = prev.trim();
      if (!message) return prev;
      if (assistantApiRef.current && showInterviewAssistant) {
        assistantApiRef.current.postMessage(message);
      } else {
        queuedPromptRef.current = message;
        setShowInterviewAssistant(true);
      }
      return '';
    });
  }, [showInterviewAssistant]);

  const handleOpenAssistant = useCallback(() => {
    setShowInterviewAssistant(true);
  }, []);

  const assistantContainerSx = {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '54px',
    bg: 'white',
    zIndex: 11,
    overflow: 'hidden',
    background:
      'linear-gradient( #fff, #fff) padding-box,linear-gradient(180deg, #8A90FF 0%, #B15FDA 100%)',
    border: '1px solid transparent',
    borderRadius: 'xs',
    visibility: showInterviewAssistant ? 'visible' : 'hidden',
    opacity: showInterviewAssistant ? 1 : 0,
    transition: 'opacity 0.2s ease-in-out',
    boxShadow:
      '32px 68px 30px rgba(191, 195, 223, 0.01), 18px 38px 25px rgba(191, 195, 223, 0.05), 8px 17px 19px rgba(191, 195, 223, 0.09), 2px 4px 10px rgba(191, 195, 223, 0.1)',
  };

  const triggerContainerSx = {
    mt: '12px',
    alignItems: 'center',
    justifyContent: 'space-between',
    p: '6px',
    background:
      'linear-gradient( #fff, #fff) padding-box,linear-gradient(180deg, #8A90FF 0%, #B15FDA 100%)',
    border: '1px solid transparent',
    borderRadius: 'xs',
    gap: '6px',
    filter:
      'drop-shadow(0px 71px 29px rgba(177, 95, 218, 0.01)) drop-shadow(0px 40px 24px rgba(177, 95, 218, 0.05)) drop-shadow(0px 18px 18px rgba(177, 95, 218, 0.09)) drop-shadow(0px 4px 10px rgba(177, 95, 218, 0.1))',
    pl: '12px',
  };

  const inputSx = {
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
    _hover: { boxShadow: 'none', border: 'none' },
    _active: { boxShadow: 'none', border: 'none' },
    _focus: { boxShadow: 'none', border: 'none', outline: 'none' },
    h: '30px',
    px: '0px',
  };

  const askAiButtonSx = {
    background:
      'linear-gradient(180deg, #8A90FF 0%, #9582F5 25.48%, #9F76EB 50%, #A86BE3 75%, #B15FDA 100%), #FFFFFF',
    _hover: {
      background:
        'linear-gradient(180deg, #8288F7 0%, #8D7AEF 25.48%, #976EE3 50%, #A063DB 75%, #A957D2 100%), #FFFFFF',
    },
    _active: {},
    _focus: {},
  };

  return (
    <>
      <Flex
        sx={assistantContainerSx}
        ref={interviewAssistantRef}
        {...assistantContainerProps}
      >
        <InterviewAssistant
          interview={interview}
          interviewId={interviewId}
          onClose={handleCloseAssistant}
          showFooter={false}
          ref={assistantApiRef}
          onLoadingChange={handleLoadingChange}
        />
      </Flex>

      {/* trigger container */}
      <Flex
        sx={triggerContainerSx}
        data-test-id="interview-assistant-cta"
        onClick={handleOpenAssistant}
        {...triggerContainerProps}
      >
        <Center>
          <Icon as={AiStar} w="16px" h="16px" />
        </Center>
        <Input
          placeholder="Ask our AI assistant for anything"
          value={externalPrompt}
          onChange={(e) => setExternalPrompt(e.target.value)}
          onFocus={handleOpenAssistant}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmitPrompt();
            }
          }}
          isDisabled={assistantLoading && showInterviewAssistant}
          sx={inputSx}
        />
        <Button
          onClick={handleSubmitPrompt}
          variant="primary"
          isDisabled={assistantLoading && showInterviewAssistant}
          sx={askAiButtonSx}
        >
          Ask AI
        </Button>
      </Flex>
    </>
  );
});
